import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import messages from './messages';
import styles from './CreateAccountWithPassword.module.scss';
import PasswordAccountCreation from '../../../components/forms/PasswordAccountCreation';
import ErrorBanner from '../../../components/common/ErrorBanner';
import ToastBanner from '../../../components/common/ToastBanner';
import {
  setShowLoginModal, setIsUserNotConfirmed, setIsAlmostDone, setUnverifiedEmail,
} from '../../../slices/accounts';
import LoginModal from '../../../components/LoginModal';
import { isSameAddressWithName } from '../../../utils';
import AuthService from '../../../services/cognito/auth';
import LinkButton from '../../../components/common/LinkButton';

const CreateAccountWithPassword = () => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const [errorMessage, setErrorMessage] = useState(null);
  const {
    showLoginModal,
    isResendEmailSubmitted,
  } = useSelector((state) => state.accounts);
  const savedEmail = useSelector((state) => state.order.customerEmail);
  const [currentSessionVerified, setCurrentSessionVerified] = useState(true);
  const [vericationMailResent, setVericationMailResent] = useState(false);

  const openLoginModal = () => {
    dispatch(setShowLoginModal({ showLoginModal: true }));
  };
  const [unverifiedAccountCreated, setUnverifiedAccountCreated] = useState(null);
  const order = useSelector((state) => state.order);
  const unverifiedEmail = useSelector(
    (state) => state.accounts.unverifiedEmail,
  );

  const customerAddress = [
    order?.shippingAddress,
    ...(!isSameAddressWithName(order?.billingAddress, order?.shippingAddress)
      ? [order?.billingAddress] : []),
  ];
  const handleError = (error, emailAddress) => {
    if (error.name === 'UserNotConfirmedException') {
      dispatch(setUnverifiedEmail({ unverifiedEmail: emailAddress }));
      dispatch(setIsUserNotConfirmed({ isUserNotConfirmed: true }));
      dispatch(setIsAlmostDone({ isAlmostDone: false }));
      setErrorMessage('');
    } else {
      dispatch(setIsUserNotConfirmed({ isUserNotConfirmed: false }));
      dispatch(setIsAlmostDone({ isAlmostDone: false }));
    }
  };

  const resendVerificationEmail = async (username) => {
    const response = await AuthService.resendConfirmationCode({ username });
    if (response?.CodeDeliveryDetails?.Destination) {
      dispatch(setIsUserNotConfirmed({ isUserNotConfirmed: false }));
      dispatch(setIsAlmostDone({ isAlmostDone: true }));
      setVericationMailResent(true);
      return;
    }
    handleError(response, username);
  };

  useEffect(() => {
    async function fetchCurrentUserInfo() {
      const response = await AuthService.currentUserInfo();
      if (response) {
        setCurrentSessionVerified(response?.attributes.email_verified);
      } else {
        setCurrentSessionVerified(false);
      }
    }
    try {
      fetchCurrentUserInfo();
    } catch (error) {
      setCurrentSessionVerified(false);
      throw error;
    }
  }, []);

  return (
    <div>
      {!currentSessionVerified && !unverifiedAccountCreated && unverifiedEmail === '' && (
        <div className={styles['create-account-with-password']}>
          <h2>{formatMessage(messages.title)}</h2>
          {errorMessage && (
            <ErrorBanner
              showError
              errorTitle={formatMessage(messages.errorTitle)}
              onCloseErrorMessage={() => setErrorMessage('')}
            >
              <>
                {formatMessage(messages.errorGenericMessage)}
                <span>{formatMessage(messages.errorPhoneMessage)}</span>
              </>
            </ErrorBanner>
          )}
          <div className={styles['create-account-password-container']}>
            <>
              <div className={styles['creat-account-password-list']}>
                <p className={styles['desktop-title']}>
                  {formatMessage(messages.prosForCreatingAccountTitleDesktop)}
                </p>
                <p className={styles['mobile-title']}>
                  {formatMessage(messages.prosForCreatingAccountTitleMobile)}
                </p>
                <ul>
                  <li key="1">
                    {formatMessage(messages.prosForCreatingAccountBullet0)}
                  </li>
                  <li key="2">
                    {formatMessage(messages.prosForCreatingAccountBullet1)}
                  </li>
                  <li key="3">
                    {formatMessage(messages.prosForCreatingAccountBullet2)}
                  </li>
                </ul>
              </div>
            </>
            <div className={styles['create-account-password-form']}>
              <p>
                {formatMessage(messages.postOrderEmailLabelConfirmation)}
                <span className={styles['email-address-create-account']} data-private>
                  {savedEmail}
                </span>
              </p>
              <PasswordAccountCreation
                accountSuccessfullyCreated={unverifiedAccountCreated}
                setAccountSuccessfullyCreated={setUnverifiedAccountCreated}
                openLoginModal={openLoginModal}
                setErrorMessage={setErrorMessage}
                customerAddress={customerAddress}
              />
            </div>
          </div>
        </div>
      )}
      {vericationMailResent ? (
        <ToastBanner
          type="warning"
          title={formatMessage(messages.sendVerifyTitle)}
        >
          {formatMessage(messages.resendVerifyMessagePt1)}
          <span className={styles['saved-email']}>{savedEmail}</span>
          {formatMessage(messages.resendVerifyMessagePt2)}
        </ToastBanner>
      ) : unverifiedAccountCreated && (
        <ToastBanner
          type="warning"
          title={formatMessage(messages.sendVerifyTitle)}
        >
          <>
            {formatMessage(messages.sendVerifyMessagePt1)}
            <span className={styles['saved-email']}>{savedEmail}</span>
            {formatMessage(messages.sendVerifyMessagePt2)}
            <span className={styles.resend}>
              <LinkButton
                id="resendButton"
                isBold
                onClick={() => resendVerificationEmail(savedEmail)}
                linkText={formatMessage(messages.unverifiedErrorPt3)}
              />
            </span>
            {formatMessage(messages.unverifiedErrorPt4)}
          </>
        </ToastBanner>
      )}
      {unverifiedEmail !== '' && (
      <ToastBanner
        type="error"
        title={formatMessage(messages.sendVerifyTitle)}
      >
        <>
          {formatMessage(messages.unverifiedErrorPt1)}
          <span className={styles['unverified-email']}>{unverifiedEmail}</span>
          {`. ${formatMessage(messages.unverifiedErrorPt2)}`}
          <span className={styles.resend}>
            <LinkButton
              id="resendButton"
              isError
              isBold
              onClick={() => resendVerificationEmail(savedEmail)}
              linkText={formatMessage(messages.unverifiedErrorPt3)}
            />
          </span>
          {formatMessage(messages.unverifiedErrorPt4)}
        </>
      </ToastBanner>
      )}
      {(showLoginModal && !isResendEmailSubmitted) && <LoginModal hideCreateAccount />}
    </div>
  );
};

export default CreateAccountWithPassword;
